/**
 * Ponte entre a página montada no servidor (`public-blog-html`) e o SPA.
 *
 * Em domínio próprio o HTML já chega com:
 *  - o conteúdo do blog dentro de `#root` (`[data-fastblog-server-content]`);
 *  - um `<script id="__FASTBLOG_DATA__" type="application/json">` com o mesmo
 *    payload que `public-blog-resolve` devolveria para aquela página.
 *
 * Aqui a gente (1) mantém o conteúdo do servidor visível enquanto o React
 * carrega — em vez do esqueleto cinza — e (2) entrega o payload para a página
 * iniciar já com dados, sem nova consulta antes da primeira pintura.
 */
import type { PublicBlogMode, PublicBlogResponse } from "@/lib/public-blog-fetch";

const DATA_ID = "__FASTBLOG_DATA__";
const SSR_HOLDER_ID = "fastblog-ssr";
const SSR_BODY_CLASS = "fb-ssr-visible";

export interface ServerBlogData extends PublicBlogResponse {
  mode: PublicBlogMode;
  host: string;
  path: string;
  postSlug?: string;
  category?: string;
  generatedAt?: string;
}

let parsed: ServerBlogData | null | undefined;
let consumed = false;

function normalizeHost(h: string): string {
  return (h || "").toLowerCase().replace(/^www\./, "");
}

/** Lê (uma vez) o payload embutido; null quando não existe ou não é desta página. */
export function readServerData(): ServerBlogData | null {
  if (parsed !== undefined) return parsed;
  parsed = null;
  if (typeof document === "undefined") return null;
  try {
    const el = document.getElementById(DATA_ID);
    if (!el?.textContent) return null;
    const data = JSON.parse(el.textContent) as ServerBlogData;
    if (!data || !data.project || !data.mode) return null;
    if (normalizeHost(data.host) !== normalizeHost(window.location.hostname)) return null;
    parsed = data;
    // O JSON já foi lido; o nó pode sair do DOM.
    el.parentElement?.removeChild(el);
  } catch {
    parsed = null;
  }
  return parsed;
}

/**
 * Entrega o payload do servidor para a página certa, uma única vez.
 * Navegações seguintes dentro do SPA não reaproveitam (seriam dados de outra rota).
 */
export function takeServerData(
  mode: PublicBlogMode,
  match: { projectId?: string | null; postSlug?: string | null; category?: string | null } = {},
): ServerBlogData | null {
  if (consumed) return null;
  const data = readServerData();
  if (!data || data.mode !== mode) return null;
  if (match.projectId && data.project?.id && data.project.id !== match.projectId) return null;
  if (mode === "post") {
    const wanted = (match.postSlug || "").trim();
    if (!wanted) return null;
    if (wanted !== data.postSlug && wanted !== data.canonicalSegment) return null;
  }
  if (mode === "category") {
    const wanted = (match.category || "").trim();
    if (!wanted || wanted !== data.category) return null;
  }
  consumed = true;
  return data;
}

/** Projeto resolvido pelo servidor para este host (sem round-trip ao banco). */
export function serverResolvedProject(): any | null {
  return readServerData()?.project || null;
}

/**
 * Chamado antes do React montar: tira o conteúdo do servidor de dentro de
 * `#root` (que o React vai esvaziar) e o mantém visível ao lado, até a página
 * real estar pronta. Sem isso o visitante vê conteúdo → esqueleto → conteúdo.
 */
export function preserveServerContent(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.getElementById("root");
  const main = root?.querySelector("[data-fastblog-server-content]");
  if (!root || !main) return false;
  try {
    const holder = document.createElement("div");
    holder.id = SSR_HOLDER_ID;
    const style = root.querySelector("style[data-fastblog-server-style]");
    if (style) holder.appendChild(style);
    holder.appendChild(main);
    root.parentElement?.insertBefore(holder, root);
    document.body.classList.add(SSR_BODY_CLASS);
    // Rede de segurança: se alguma rota esquecer de liberar a casca, ela sai
    // sozinha depois de um tempo — a página React nunca fica escondida.
    window.setTimeout(() => {
      if (document.getElementById(SSR_HOLDER_ID)) dismissServerContent();
    }, 15000);
    return true;
  } catch {
    return false;
  }
}

export function hasServerContent(): boolean {
  return typeof document !== "undefined" && !!document.getElementById(SSR_HOLDER_ID);
}

/** Remove o conteúdo do servidor assim que a página React está pronta. */
export function dismissServerContent(): void {
  if (typeof document === "undefined") return;
  const holder = document.getElementById(SSR_HOLDER_ID);
  if (holder) holder.parentElement?.removeChild(holder);
  document.body.classList.remove(SSR_BODY_CLASS);
}
